import { useEffect, useId, type ReactNode } from 'react';

/**
 * A dialog over the app.
 *
 * Closing is optional on purpose. Adapter setup has no close control for
 * somebody who can add an adapter, because the whole point is that a scan
 * cannot start until one exists; the simulator is their way out instead. Leave
 * onClose off and neither Escape nor a backdrop click dismisses it.
 */
export function Modal({ title, onClose, children }: { title: string; onClose?: () => void; children: ReactNode }) {
  const titleId = useId();

  useEffect(() => {
    if (!onClose) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="modal-backdrop"
      onMouseDown={(e) => {
        if (onClose && e.target === e.currentTarget) onClose();
      }}
    >
      <div className="modal" role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <div className="modal-head">
          <h2 id={titleId}>{title}</h2>
          {onClose && (
            <button className="tiny ghost" onClick={onClose} aria-label="Close">
              close
            </button>
          )}
        </div>
        <div className="modal-body">{children}</div>
      </div>
    </div>
  );
}
